import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { setTourActive } from '@/store/core.slice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';

interface TourStep {
  anchor: string;
  textKey: string;
}

/**
 * Ordered coach-marks. A step whose anchor is missing from the DOM (feature
 * flag off, error state) is skipped — the tour must survive Hub evolution.
 */
const STEPS: TourStep[] = [
  { anchor: '[data-tour="hub-sections"]', textKey: 'tour.stepSections' },
  { anchor: '[data-tour="hub-section-events"]', textKey: 'tour.stepEvents' },
  { anchor: '[data-tour="currency-hud"]', textKey: 'tour.stepCurrency' },
  { anchor: '[data-tour="tab-news"]', textKey: 'tour.stepTabs' },
  { anchor: '[data-tour="tab-settings"]', textKey: 'tour.stepProfile' },
];

const PAD = 6; // px of breathing room around the spotlit rect

/**
 * Lightweight spotlight overlay (no lib): fixed backdrop with a cut-out via
 * oversized box-shadow, tooltip above/below the target, Next/Skip. Driven by
 * `core.tourActive`; measures on demand and on resize.
 */
export function HubTour() {
  const active = useAppSelector((s) => s.core.tourActive);
  const dispatch = useAppDispatch();
  const { t } = useTranslation('onboarding');
  const haptics = useHaptics();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      setRect(null);
      return;
    }
    const measure = () => {
      // Find the first step at/after `index` whose anchor exists.
      let i = index;
      let el: Element | null = null;
      while (i < STEPS.length) {
        el = document.querySelector(STEPS[i].anchor);
        if (el) break;
        i += 1;
      }
      if (!el) {
        dispatch(setTourActive(false));
        return;
      }
      if (i !== index) {
        setIndex(i);
        return; // effect re-runs with the skipped-to index
      }
      setRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active, index, dispatch]);

  if (!active || !rect) return null;

  const isLast =
    STEPS.slice(index + 1).every((s) => !document.querySelector(s.anchor));
  const next = () => {
    haptics.selection();
    if (isLast) dispatch(setTourActive(false));
    else setIndex((i) => i + 1);
  };
  const skip = () => {
    haptics.selection();
    dispatch(setTourActive(false));
  };

  // Tooltip above the target when the target sits in the lower half.
  const tooltipAbove = rect.top > window.innerHeight / 2;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Spotlight: transparent window over the target, dimmed everywhere else. */}
      <div
        className="absolute rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.72)] transition-all duration-200"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        }}
      />
      <div
        className="absolute left-4 right-4 flex flex-col gap-3 rounded-2xl bg-bg-secondary p-4"
        style={
          tooltipAbove
            ? { bottom: window.innerHeight - rect.top + PAD * 2 }
            : { top: rect.bottom + PAD * 2 }
        }
      >
        <p className="text-sm text-fg">{t(STEPS[index].textKey)}</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={skip}
            className="text-xs font-semibold text-fg-muted underline"
          >
            {t('tour.skip')}
          </button>
          <button
            type="button"
            onClick={next}
            className="rounded-xl bg-brand px-4 py-2 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
          >
            {isLast ? t('tour.done') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
